/**
 * Nether runtime glue (one instance in main.ts): fireballs (sim + sprites),
 * enderman stare detection, portal particles, nether wart growth, blaze
 * spawners and water evaporation. Keeps main.ts edits to a few calls.
 */

import * as THREE from 'three';
import {
  AIR, BLOCKS, IS_SOLID, isNetherPortal, NETHER_WART_IDS, SOUL_SAND_ID, SPAWNER_ID,
} from '../blocks';
import type { ChunkManager } from '../world/manager';
import type { EntityManager, Mob } from '../entities';
import { Fireballs } from '../entities/nether/fireballs';
import { fireballHook, Enderman } from '../entities/nether/mobs2';
import { netherHooks } from '../entities/nether/mobs';
import { FireballRenderer } from '../render/fireballs';
import type { EnvUniforms } from '../render/env';
import { lineClear } from '../entities/host';
import { itemSpriteMesh } from '../render/entities/extras';

export interface NetherRuntimeHost {
  chunks: ChunkManager;
  entities: EntityManager;
  env: EnvUniforms;
  scene: THREE.Object3D;
  camera: THREE.Camera;
  player: { position: THREE.Vector3; velocity: THREE.Vector3; alive: () => boolean };
  hurtPlayer(amount: number, x: number, y: number, z: number, fireTicks: number): void;
  explode(x: number, y: number, z: number, power: number): void;
  portalParticle(id: number, x: number, y: number, z: number, tx: number, ty: number, tz: number): void;
  /** Water evaporation puff + hiss. */
  evaporate?(x: number, y: number, z: number): void;
  /** Ender pearl landed: move the player there (with 5 fall damage). */
  pearlTeleport?(x: number, y: number, z: number): void;
  /** A pearl flew into an End Gateway block: true when it warped the player. */
  pearlGateway?(x: number, y: number, z: number): boolean;
}

interface Pearl { x: number; y: number; z: number; px: number; py: number; pz: number; vx: number; vy: number; vz: number; age: number; mesh: THREE.Mesh }

const dir = new THREE.Vector3();

export class NetherRuntime {
  readonly fireballs = new Fireballs();
  private readonly renderer: FireballRenderer;
  private spawnerCd = new Map<string, number>();
  private pearls: Pearl[] = [];
  private pearlCd = 0;

  constructor(private readonly h: NetherRuntimeHost) {
    this.renderer = new FireballRenderer(h.env);
    h.scene.add(this.renderer.group);
    fireballHook.shoot = (small, x, y, z, dx, dy, dz, owner) => { this.fireballs.spawn(small, x, y, z, dx, dy, dz, owner, Math.random); };
    netherHooks.setBlock = (x, y, z, id) => {
      if (!h.chunks.canEdit(x, y, z)) return false;
      h.chunks.setBlock(x, y, z, id);
      return true;
    };
    netherHooks.toss = (vy) => { h.player.velocity.y = Math.max(h.player.velocity.y, vy); };
    netherHooks.effect = (_k, x, y, z) => {
      for (let i = 0; i < 24; i++) {
        h.portalParticle(BLOCKS.findIndex((b) => b?.name === 'Nether Portal'),
          x + (Math.random() - 0.5) * 1.2, y + (Math.random() - 0.5) * 2.4, z + (Math.random() - 0.5) * 1.2, x, y, z);
      }
    };
  }

  /** Called on a dimension switch. */
  clear(): void {
    this.fireballs.clear(); this.spawnerCd.clear();
    for (const p of this.pearls) p.mesh.removeFromParent();
    this.pearls.length = 0;
  }

  /** Throw an ender pearl from the eye along `d` (false while on cooldown). */
  throwPearl(eye: THREE.Vector3, d: THREE.Vector3): boolean {
    if (this.pearlCd > 0) return false;
    this.pearlCd = 20;
    const { mesh } = itemSpriteMesh(this.h.env, 'ender_pearl', true);
    mesh.scale.setScalar(0.5);
    this.h.scene.add(mesh);
    this.pearls.push({
      x: eye.x + d.x * 0.3, y: eye.y + d.y * 0.3, z: eye.z + d.z * 0.3, px: eye.x, py: eye.y, pz: eye.z,
      vx: d.x * 1.5, vy: d.y * 1.5, vz: d.z * 1.5, age: 0, mesh,
    });
    return true;
  }

  private tickPearls(): void {
    if (this.pearlCd > 0) this.pearlCd--;
    const w = this.h.chunks.world;
    for (const p of this.pearls) {
      p.px = p.x; p.py = p.y; p.pz = p.z;
      p.age++;
      let hit = false;
      for (let s = 0; s < 4 && !hit; s++) {
        p.x += p.vx / 4; p.y += p.vy / 4; p.z += p.vz / 4;
        if (this.h.pearlGateway?.(Math.floor(p.x), Math.floor(p.y), Math.floor(p.z))) { p.mesh.removeFromParent(); p.age = -1; hit = false; break; }
        if (IS_SOLID[w.getBlock(Math.floor(p.x), Math.floor(p.y), Math.floor(p.z))] === 1) {
          // back out of the block the pearl struck
          p.x -= p.vx / 4; p.y -= p.vy / 4; p.z -= p.vz / 4;
          hit = true;
        }
        for (const m of this.h.entities.mobs) {
          if (m.alive && Math.abs(m.x - p.x) < m.width / 2 + 0.15 && Math.abs(m.z - p.z) < m.width / 2 + 0.15 && p.y > m.y && p.y < m.y + m.height) hit = true;
        }
      }
      // vanilla thrown-item physics: gravity 0.03, drag 0.99
      p.vy -= 0.03; p.vx *= 0.99; p.vy *= 0.99; p.vz *= 0.99;
      if (p.age < 0) continue;
      if (hit || p.y < -10 || p.age > 400) {
        if (hit) {
          for (let i = 0; i < 32; i++) {
            this.h.portalParticle(BLOCKS.findIndex((b) => b?.name === 'Nether Portal'),
              p.x + (Math.random() - 0.5), p.y + Math.random() * 2, p.z + (Math.random() - 0.5), p.x, p.y + 1, p.z);
          }
          this.h.pearlTeleport?.(p.x, Math.floor(p.y + 0.01), p.z);
        }
        p.mesh.removeFromParent();
        p.age = -1;
      }
    }
    this.pearls = this.pearls.filter((p) => p.age >= 0);
  }

  /** Left click: punch a ghast fireball back (true = the click was used). */
  deflect(eye: THREE.Vector3, d: THREE.Vector3, reach: number): boolean {
    return this.fireballs.deflect(eye.x, eye.y, eye.z, d.x, d.y, d.z, reach);
  }

  /** Is water allowed here? (vanilla: ultrawarm dimensions evaporate it) */
  waterEvaporates(): boolean { return this.h.chunks.dimension === 'nether'; }

  tick(): void {
    const h = this.h;
    const w = h.chunks.world;
    const p = h.player.position;
    this.fireballs.tick({
      world: w,
      player: { x: p.x, y: p.y, z: p.z, width: 0.6, height: 1.8, alive: h.player.alive() },
      hurtPlayer: (a, x, y, z, fire) => h.hurtPlayer(a, x, y, z, fire),
      mobs: () => h.entities.mobs,
      hurtMob: (m, a, fx, fz) => { h.entities.attack(m, a, fx, fz, 0.4); },
      explode: (x, y, z, power) => h.explode(x, y, z, power),
      canEdit: (x, y, z) => h.chunks.canEdit(x, y, z),
      setBlock: (x, y, z, id) => { h.chunks.setBlock(x, y, z, id); },
      rnd: Math.random,
    });
    this.stare();
    this.randomTicks();
    this.tickPearls();
  }

  frame(alpha: number): void {
    this.renderer.update(this.fireballs.list, alpha, this.h.camera);
    for (const p of this.pearls) {
      p.mesh.position.set(p.px + (p.x - p.px) * alpha, p.py + (p.y - p.py) * alpha, p.pz + (p.z - p.pz) * alpha);
      p.mesh.quaternion.copy(this.h.camera.quaternion);
    }
  }

  /** Endermen anger when the crosshair rests on their head (vanilla isLookingAtMe). */
  private stare(): void {
    const h = this.h;
    h.camera.getWorldDirection(dir);
    const eye = h.camera.position;
    for (const m of h.entities.mobs) {
      if (!(m instanceof Enderman) || !m.alive) continue;
      const dx = m.x - eye.x, dy = m.y + m.eyeHeight - eye.y, dz = m.z - eye.z;
      const d = Math.hypot(dx, dy, dz);
      if (d > 64 || d < 0.01) continue;
      const dot = (dx * dir.x + dy * dir.y + dz * dir.z) / d;
      if (dot > 1 - 0.025 / d && lineClear(h.chunks.world, eye.x, eye.y, eye.z, m.x, m.y + m.eyeHeight, m.z)) m.stared = true;
    }
  }

  /** Random ticks around the player: portal particles, wart growth, spawners. */
  private randomTicks(): void {
    const h = this.h;
    const w = h.chunks.world;
    const p = h.player.position;
    for (let i = 0; i < 64; i++) {
      const x = Math.floor(p.x + (Math.random() - 0.5) * 32);
      const y = Math.floor(p.y + (Math.random() - 0.5) * 24);
      const z = Math.floor(p.z + (Math.random() - 0.5) * 32);
      const id = w.getBlock(x, y, z);
      if (id === AIR) continue;
      if (isNetherPortal(id)) {
        // vanilla animateTick: particles spiral in toward the pane
        for (let k = 0; k < 3; k++) {
          const sx = x + 0.5 + (Math.random() - 0.5) * 2, sy = y + Math.random(), sz = z + 0.5 + (Math.random() - 0.5) * 2;
          h.portalParticle(id, sx, sy, sz, x + 0.5, y + 0.5 + (Math.random() - 0.5) * 0.5, z + 0.5);
        }
      } else if (id === SPAWNER_ID) {
        this.spawner(x, y, z);
      } else {
        const age = NETHER_WART_IDS.indexOf(id);
        // vanilla: 10% per random tick, only on soul sand
        if (age >= 0 && age < 3 && w.getBlock(x, y - 1, z) === SOUL_SAND_ID && Math.random() < 0.1 * 3) {
          h.chunks.setBlock(x, y, z, NETHER_WART_IDS[age + 1]);
        }
      }
    }
    // spawners near the player tick even when not sampled this tick
    for (const [k, t] of this.spawnerCd) if (t > 0) this.spawnerCd.set(k, t - 1);
  }

  /** Blaze spawner: player within 16, 200-800 tick delay, up to 4 blazes, max 6 nearby. */
  private spawner(x: number, y: number, z: number): void {
    const h = this.h;
    const p = h.player.position;
    const dx = x + 0.5 - p.x, dy = y + 0.5 - p.y, dz = z + 0.5 - p.z;
    if (dx * dx + dy * dy + dz * dz > 256) return;
    const key = x + ',' + y + ',' + z;
    const cd = this.spawnerCd.get(key) ?? 0;
    if (cd > 0) return;
    this.spawnerCd.set(key, 200 + Math.floor(Math.random() * 600) / 1); // decremented per tick above
    let near = 0;
    for (const m of h.entities.mobs as readonly Mob[]) if (m.type === 'blaze' && Math.abs(m.x - x) < 9 && Math.abs(m.z - z) < 9) near++;
    if (near >= 6) return;
    const n = 1 + Math.floor(Math.random() * 4);
    for (let i = 0; i < n; i++) {
      const sx = x + 0.5 + (Math.random() - Math.random()) * 4, sz = z + 0.5 + (Math.random() - Math.random()) * 4;
      const sy = y + Math.floor(Math.random() * 3) - 1;
      if (h.chunks.world.getBlock(Math.floor(sx), sy, Math.floor(sz)) !== AIR) continue;
      h.entities.spawnMob('blaze', sx, sy, sz);
    }
  }
}
